// DynamicTable.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  AutoCompleteProps,
  Button,
  Input,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import debounce from 'lodash/debounce';

import { InputNumberForm } from '@/components/form/InputNumber';
import { SelectForm } from '@/components/form/Select';
import { useAppSelector } from '@/core/redux/hooks';
import { systemSelectors } from '@/core/redux/slices/smokeExtraction/system/selectors';

import { resolverColumns } from './resolver/columns';

import styles from './styles.module.scss';

const { Text } = Typography;

interface IDynamicAerodynamicsTable {
  handleChange: () => void;
  isObserver: boolean;
}

export const DynamicAerodynamicsTable: React.FC<IDynamicAerodynamicsTable> = ({
  handleChange,
  isObserver,
}) => {
  const { control, reset, setValue, watch } = useFormContext();
  const systemState = useAppSelector(systemSelectors.systemState);

  const [isHideSupport, setIsHideSupport] = useState<boolean>(false);
  const [isRoundingValues, setIsRoundingValues] = useState<boolean>(false);

  const { fields, remove, insert } = useFieldArray({
    control,
    name: 'result_table',
  });

  const mode = systemState?.mode;
  const P_kon_uch_threshold = systemState.P_kon_uch_threshold;
  const v_threshold = systemState.v_threshold;
  const is_bim_imported = systemState.is_bim_imported;

  const getMax = (item: any) => {
    if (item?.name === 'P_kon_uch' && P_kon_uch_threshold) {
      return P_kon_uch_threshold;
    }

    if (item?.name === 'v' && v_threshold) {
      return v_threshold;
    }
    return undefined;
  };

  const handleAddRow = (index: number) => {
    const nullValuesObject: Record<string, null> = {};

    resolverColumns.forEach((column) => {
      nullValuesObject[column.name] = null;
    });

    insert(index + 1, {
      ...nullValuesObject,
      ql: 0,
      i: fields?.length + 1,
    });
    handleChange();
  };

  const handleCopyRow = (index: number) => {
    const rowToCopy: Record<string, string> = fields[index]; // Get the row to copy

    insert(index + 1, {
      ...rowToCopy,
      G_nach_uch: rowToCopy['G_kon_uch'],
      t_nach_uch: rowToCopy['t_kon_uch'],
      i: fields.length + 1,
    });
    handleChange();
  };

  const handleRemoveRow = (index: number) => {
    remove(index);
    handleChange();
  };

  const filteredColumns = useMemo(
    () =>
      resolverColumns.filter((item: any) => {
        if (isHideSupport) {
          return item.editable;
        }

        return true;
      }),
    [resolverColumns, isHideSupport, isRoundingValues]
  );

  useEffect(() => {
    reset(systemState);
  }, [isHideSupport]);

  const [autoCompleteOptions] = useState<AutoCompleteProps['options']>([
    {
      key: 'Листовая сталь',
      value: '0.1',
      label: 'Листовая сталь - 0,1мм',
    },
    {
      key: 'Винипласт',
      value: '0.1',
      label: 'Винипласт - 0,1мм',
    },
    {
      key: 'Асбестоцементные плиты или трубы',
      value: '0.11',
      label: 'Асбестоцементные плиты или трубы - 0,11мм',
    },
    {
      key: 'Шлакоалебастровые плиты',
      value: '1',
      label: 'Шлакоалебастровые плиты - 1мм',
    },
    {
      key: 'Шлакобетонные плиты',
      value: '1.5',
      label: 'Шлакобетонные плиты - 1,5мм',
    },
    {
      key: 'Кирпич',
      value: '4',
      label: 'Кирпич - 4мм',
    },
    {
      key: 'Штукатурка (по металлической сетке)',
      value: '10',
      label: 'Штукатурка (по металлической сетке) - 10мм',
    },
  ]);

  const handleSelect = (event: any, index: number) => {
    if (event === 'Не учитывать') {
      setValue(`result_table[${index}].Akl`, 0);
      setValue(`result_table[${index}].Bkl`, 0);
      setValue(`result_table[${index}].Sd20`, 0);
      setValue(`result_table[${index}].Sd20`, 0);
      setValue(`result_table[${index}].tightness_class`, 'B');
      setValue(`result_table[${index}].Ffas`, 0);
      setValue(`result_table[${index}].t_pods`, 0);
    }

    handleChange();
  };

  const checkBlurFunction = (
    index: number,
    key: string,
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const obj: any = fields[index];
    if (obj[key] === null && event?.target?.value === '') return;
    if (obj[key] !== event?.target?.value) {
      handleChange();
    }
  };

  const blurFunction = debounce(checkBlurFunction, 1000);

  const columnValues: any = filteredColumns.map((item: any, index) => {
    switch (item.type) {
      case 'Ke':
        return {
          title: (
            <Tooltip placement='top' title={item.tooltip} zIndex={1000}>
              <i className='cursor-default p-2 whitespace-nowrap block'>
                {item.title}
              </i>
            </Tooltip>
          ),
          width: 80,
          dataIndex: item.name,
          render: (text: string, record: any, index: number) => (
            <Controller
              render={({ field }) => (
                <AutoComplete
                  options={autoCompleteOptions}
                  style={{ width: 370 }}
                  allowClear={false}
                  onSelect={(value) => {
                    setValue(`result_table[${index}].${item.name}`, value);
                    handleChange();
                  }}
                  value={field.value}
                  disabled={isObserver}
                >
                  <Input
                    {...field}
                    onBlur={(event) => blurFunction(index, item?.name, event)}
                    className='!w-[70px]'
                    onKeyDown={(e) => {
                      if (e.key === ',') {
                        e.preventDefault(); // Prevent the comma from being typed
                      }
                    }}
                    disabled={isObserver}
                  />
                </AutoComplete>
              )}
              control={control}
              name={`result_table[${index}].${item.name}`}
            />
          ),
        };
      case 'enum':
        const selectOptions: any = item?.options
          ? Object.values(item.options).map((item) => ({
              value: item,
              label: item,
            }))
          : [];

        return {
          title: (
            <Tooltip placement='top' title={item.tooltip} zIndex={1000}>
              <i className='cursor-default p-2 block'>{item.title}</i>
            </Tooltip>
          ),
          width: 150,
          dataIndex: item.name,
          render: (text: string, record: any, index: number) => (
            <SelectForm
              options={selectOptions}
              name={`result_table[${index}].${item.name}`}
              onChange={(event) => handleSelect(event, index)}
              disabled={isObserver}
            />
          ),
        };

      case 'has_max':
        return {
          title: (
            <Tooltip placement='top' title={item.tooltip}>
              <div className='flex items-center'>
                <i className='cursor-default p-2 whitespace-nowrap block'>
                  {item.title}
                </i>
                {item?.name === 'P_kon_uch' && (
                  <div className='min-w-[120px] p-2'>
                    <InputNumberForm
                      name='P_kon_uch_threshold'
                      onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                        blurFunction(index, item?.name, event)
                      }
                      size='middle'
                      fixValue={3}
                      disabled={isObserver}
                    />
                  </div>
                )}
                {item?.name === 'v' && (
                  <InputNumberForm
                    name='v_threshold'
                    onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                      blurFunction(index, item?.name, event)
                    }
                    size='middle'
                    fixValue={2}
                    disabled={isObserver}
                  />
                )}
              </div>
            </Tooltip>
          ),
          width: item?.name === 'v' ? 145 : 195,
          dataIndex: item.name,
          render: (text: string, record: any, index: number) => (
            <InputNumberForm
              max={getMax(item)}
              name={`result_table[${index}].${item.name}`}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                blurFunction(index, item?.name, event)
              }
              disabled={!item.editable || isObserver}
              size='middle'
              fixValue={isRoundingValues ? 3 : item.precision}
            />
          ),
        };

      default:
        if (
          (mode === 'м3/ч' &&
            !['G_nach_uch', 'Gdop', 'Gsum', 'G_kon_uch']?.includes(
              item.name
            )) ||
          (mode === 'кг/с' &&
            !['L_nach_uch', 'Ldop', 'Lsum', 'L_kon_uch']?.includes(item.name))
        ) {
          return {
            title: (
              <Tooltip placement='top' title={item.tooltip}>
                <div className='flex items-center'>
                  <i className='cursor-default p-2 whitespace-nowrap block'>
                    {item.title}
                  </i>
                </div>
              </Tooltip>
            ),
            width: 115,
            dataIndex: item.name,
            render: (text: string, record: any, index: number) => (
              <InputNumberForm
                name={`result_table[${index}].${item.name}`}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                  blurFunction(index, item?.name, event)
                }
                disabled={!item.editable || isObserver}
                size='middle'
                fixValue={isRoundingValues ? 3 : item.precision}
              />
            ),
          };
        } else {
          return null;
        }
    }
  });

  const columns = [
    {
      title: (
        <Tooltip placement='top' title='Номер участка'>
          <div>
            <i className='cursor-default'>Уч.</i>
          </div>
        </Tooltip>
      ),
      width: 45,
      align: 'center',
      dataIndex: 'i',
      render: (text: string, record: any, index: number) => (
        <Text className='text-nowrap'>{index + 1}</Text>
      ),
    },
    is_bim_imported
      ? {
          title: 'Наименование',
          dataIndex: 'sys_elem',
          width: 200,
          render: (text: string, record: any, index: number) => text,
        }
      : null,
    ...columnValues,
    is_bim_imported
      ? {
          title: 'ID',
          dataIndex: 'elem_id',
          width: 300,
          render: (text: string, record: any, index: number) => text,
        }
      : null,
    {
      title: '',
      dataIndex: 'i',
      align: 'center',
      width: 122,
      fixed: 'right',
      render: (text: string, record: any, index: number) => (
        <div className='flex gap-2'>
          <Button
            icon={<PlusOutlined />}
            onClick={() => handleAddRow(index)}
            disabled={isObserver}
          />
          <Button
            disabled={fields?.length === 1 || isObserver}
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveRow(index)}
          />
          <Button
            disabled={isObserver}
            icon={<CopyOutlined />}
            onClick={() => handleCopyRow(index)}
          />
        </div>
      ),
    },
  ].filter((el) => el);

  return (
    <div className='flex flex-col gap-4'>
      {!isObserver && (
        <div className='flex justify-between'>
          <Button
            onClick={() => setIsHideSupport((prevState) => !prevState)}
            type='primary'
          >
            {isHideSupport
              ? 'Развернуть вспомогательные столбцы'
              : 'Свернуть вспомогательные столбцы'}
          </Button>

          <Button onClick={() => setIsRoundingValues(!isRoundingValues)}>
            Округлять значения
          </Button>
        </div>
      )}
      <div className='relative'>
        <Table
          size='small'
          dataSource={fields}
          columns={columns}
          pagination={false}
          scroll={{ x: 500 }}
          rowKey={(record) => record?.id}
          className={styles.aerotable}
        />
      </div>
    </div>
  );
};
